import { ContactForm } from '../../components/forms/ContactForm';
import { DemoPopup } from '../../components/forms/DemoPopup';
import { Button } from '../../components/ui/button';
import {
  Users,
  UserPlus,
  DollarSign,
  Clock,
  Calendar,
  LineChart,
  UserCheck,
  CheckCircle2,
} from 'lucide-react';

const features = [
  {
    icon: UserPlus,
    title: 'Recruitment & Onboarding',
    description: 'Job postings, applicant tracking, interview scheduling, and seamless onboarding workflows.',
  },
  {
    icon: DollarSign,
    title: 'Payroll Management',
    description: 'Automated payroll processing, tax calculations, and compliance with local regulations.',
  },
  {
    icon: Clock,
    title: 'Attendance & Time',
    description: 'Digital attendance tracking, shift management, and overtime calculations.',
  },
  {
    icon: Calendar,
    title: 'Leave Management',
    description: 'Configure leave policies, approval workflows, and holiday calendars.',
  },
  {
    icon: LineChart,
    title: 'Performance Reviews',
    description: '360° feedback, goal setting, KPI tracking, and performance analytics.',
  },
  {
    icon: UserCheck,
    title: 'Employee Self-Service',
    description: 'Empower employees with self-service for leaves, documents, and profile updates.',
  },
];

const benefits = [
  'Reduce HR administrative tasks by 60%',
  'Streamline entire employee lifecycle',
  'Ensure compliance with labor laws',
  'Improve employee engagement and satisfaction',
  'Make data-driven HR decisions',
  'Integrate with existing systems seamlessly',
];

export const HRPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="hr-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="hr-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              HR Management
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Complete HR Management Solution
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Streamline your entire HR lifecycle from recruitment to retirement with our comprehensive platform.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Request Demo for HR Management"
                formType="HR Management Demo"
                defaultSolution="HR Management"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="hr-hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32" data-testid="hr-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Key Features
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Everything you need to manage your workforce effectively
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`hr-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="hr-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Transform Your HR Operations
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our HR Management solution helps you build a more efficient, engaged, and productive workforce.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80"
                  alt="HR Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="py-24 md:py-32" data-testid="hr-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Request a Demo for HR Management
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="hr-demo-form-wrapper">
              <ContactForm
                formType="HR Management Demo"
                showInterestedIn={false}
                showCompanySize={true}
                interestedInDefault="HR Management"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HRPage;
